<template>
  <!-- Conteneur principal centré -->
  <div class="flex justify-center w-full">
    <div class="w-[1200px]">
      <!-- Contrôles externes -->
      <div class="bg-white p-4 border-b border-gray-200 rounded-t-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button
              class="btn-control"
              :disabled="
                !supportStore.currentSupport?.products ||
                  supportStore.currentSupport.products.length <= 1
              "
              @click="decreaseProducts"
            >
              <span class="text-xl">-</span>
            </button>
            <span class="text-lg font-medium">{{ nbProducts }} produit{{ nbProducts > 1 ? "s" : "" }}</span>
            <button
              class="btn-control"
              :disabled="
                !supportStore.currentSupport?.products ||
                  supportStore.currentSupport.products.length >= 6
              "
              @click="increaseProducts"
            >
              <span class="text-xl">+</span>
            </button>
            
            <!-- Contrôles taille titre -->
            <div class="flex items-center gap-2 ml-4">
              <span class="text-sm text-gray-600">Taille titre:</span>
              <button
                class="btn-control text-sm"
                :disabled="currentSizeIndex <= 0"
                title="Diminuer la taille du titre"
                @click="decreaseTitleSize"
              >
                <span class="text-lg">T-</span>
              </button>
              <span class="text-sm font-medium min-w-[40px] text-center">{{ selectedSize }}</span>
              <button
                class="btn-control text-sm"
                :disabled="currentSizeIndex >= titleSizes.length - 1"
                title="Augmenter la taille du titre"
                @click="increaseTitleSize"
              >
                <span class="text-lg">T+</span>
              </button>
            </div>
          </div>

          <!-- Boutons de contrôle -->
          <div class="flex items-center gap-2">
            <!-- Bouton aperçu -->
            <button
              class="preview-toggle"
              :class="{ active: isPreviewMode }"
              @click="supportStore.togglePreview()"
            >
              <span class="icon">👁️</span>
              {{ isPreviewMode ? "Mode édition" : "Aperçu" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Toile -->
      <div
        class="support-canvas relative bg-white shadow-lg"
        :class="{ 'preview-mode': isPreviewMode }"
      >
        <!-- Zone de titre -->
        <div
          class="support-title-container p-2"
          :style="{
            backgroundColor: brand === 'fendt' ? '#005e1e' : '#000000',
          }"
        >
          <h2
            class="support-title-text text-center text-6xl font-bold"
            style="color: #ffffff"
            contenteditable="true"
            @blur="updateTitle"
            v-text="titleText"
          />
        </div>

        <!-- Zone de contenu -->
        <div class="support-content">
          <div
            class="products-container"
            :class="{ 'flex-col': nbProducts > 1 }"
          >
            <div
              v-for="index in Math.min(nbProducts, 3)"
              :key="index"
              class="product-zone w-full"
              @dragover.prevent="isDraggingOver = index - 1"
              @dragleave.prevent="isDraggingOver = null"
              @drop.prevent="onDrop($event, index - 1)"
            >
              <ProductBlock
                :product="getProduct(index - 1)"
                :position="index"
                :is-preview="isPreviewMode"
                :font-family="selectedFont"
                :font-size="selectedSize"
                :is-social-network="true"
                :is-main-product="index === 1"
                variant="rowOne"
                @remove="removeProduct(index - 1)"
                @update:product="handleProductUpdate(index - 1, $event)"
              />
            </div>
          </div>
        </div>

        <!-- Pied de page -->
        <div class="support-footer bg-white h-12 flex items-center">
          <div class="flex items-center gap-8 pl-8 w-full">
            <!-- Logo Valtra -->
            <div v-if="brand === 'valtra'" class="flex-shrink-0">
              <img
                :src="`/images/logo-${brand}.svg`"
                :alt="brand"
                class="h-6"
              >
            </div>

            <!-- Période de validité -->
            <div class="text-lg flex items-center gap-2 validity-section">
              <div class="validity-text-container">
                <span
                  v-if="showValidityPeriod"
                  ref="validityRef"
                  :class="[
                    'outline-none inline-block bg-white',
                    brand === 'fendt' ? 'text-[#4B7526]' : 'text-red-600',
                  ]"
                  contenteditable="true"
                  draggable="false"
                  @blur="updateValidityPeriod"
                  @dragover.prevent
                  @dragleave.prevent
                  @drop.prevent
                >{{ validityPeriod }}</span>
              </div>
              <button
                v-if="!isPreviewMode"
                class="toggle-validity-button"
                :title="
                  showValidityPeriod
                    ? 'Masquer la période'
                    : 'Afficher la période'
                "
                @click="showValidityPeriod = !showValidityPeriod"
              >
                <span class="icon">{{ showValidityPeriod ? "👁️" : "🚫" }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HIGH_QUALITY_SCALE, PDF_IMAGE_COMPRESSION } from "@/utils/pdfQualityConfig";
import { useProductCustomizationStore } from "@/stores/supports/useProductCustomizationStore";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import {
  calculateSocialMediaDimensions,
  getSocialMediaPixelDimensions,
} from "@/utils/supportUtils";
import { computed, onMounted, ref } from "@vue/runtime-core";
import ProductBlock from "../blocks/ProductBlock.vue";

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference?: string;
  image_name?: string;
}

const supportStore = useSupportStore();
const productCustomizationStore = useProductCustomizationStore();
const isDraggingOver = ref<number | null>(null);
const selectedFont = ref<string>("Arial");
const selectedSize = ref<string>("md");
const isPreviewMode = computed<boolean>(() => supportStore.isPreview);
const titleText = ref<string>("Votre titre ici");
const brand = ref<string>(localStorage.getItem("brand") || "agco");
const validityRef = ref<HTMLElement | null>(null);
const showValidityPeriod = ref<boolean>(true);
const validityPeriod = ref<string>("du XX au XX mois ANNÉE");

// Nombre total de slots de produits (pas seulement les produits remplis)
const nbProducts = computed<number>(() => {
  const products = supportStore.currentSupport?.products;
  return products?.length || 1;
});

const getProduct = (index: number): Product | null => {
  const products = supportStore.currentSupport?.products;
  return products?.[index] || null;
};

const increaseProducts = (): void => {
  if (!supportStore.currentSupport) return;
  const products = supportStore.currentSupport.products;

  if (!Array.isArray(products)) {
    supportStore.currentSupport.products = [];
  }

  if (products.length < 3) {
    const newProducts = [...products];
    newProducts.push(null);
    supportStore.currentSupport.products = newProducts;
  }
};

const decreaseProducts = (): void => {
  if (!supportStore.currentSupport) return;
  const products = supportStore.currentSupport.products;

  if (products && products.length > 1) {
    const newProducts = products.slice(0, -1);
    supportStore.currentSupport.products = newProducts;
  }
};

const removeProduct = (index: number): void => {
  if (!supportStore.currentSupport) return;
  const products = supportStore.currentSupport.products;

  if (products) {
    supportStore.currentSupport.products[index] = null;
  }
};

const onDrop = (event: DragEvent, index: number): void => {
  isDraggingOver.value = null;
  const productData = event.dataTransfer?.getData("application/json");
  if (!productData) return;

  try {
    const product = JSON.parse(productData) as Product;
    if (supportStore.currentSupport) {
      const products = supportStore.currentSupport.products;

      if (!Array.isArray(products)) {
        supportStore.currentSupport.products = [];
      }

      while (products.length <= index) {
        supportStore.currentSupport.products.push(null);
      }

      supportStore.currentSupport.products[index] = product;
    }
  } catch (error) {
    console.error("Invalid product data:", error);
  }
};

const handleProductUpdate = (index: number, product: Product): void => {
  updateProduct(index, product);
};

const updateProduct = (index: number, product: Product): void => {
  if (!supportStore.currentSupport) return;
  const products = supportStore.currentSupport.products;

  if (!Array.isArray(products)) {
    supportStore.currentSupport.products = [];
  }

  while (products.length <= index) {
    supportStore.currentSupport.products.push(null);
  }

  supportStore.currentSupport.products[index] = product;
};

onMounted((): void => {
  if (validityRef.value) {
    validityRef.value.textContent = `Offres valables ${validityPeriod.value}`;
  }

  // S'assurer que les produits sont bien initialisés depuis le store
  if (
    supportStore.currentSupport &&
    (!supportStore.currentSupport.products ||
      supportStore.currentSupport.products.length === 0)
  ) {
    supportStore.currentSupport.products = [null];
  }

  // Forcer l'extraction des produits depuis les éléments si nécessaire
  if (supportStore.currentSupport && supportStore.currentSupport.pages) {
    const products: any[] = [];
    for (const page of supportStore.currentSupport.pages) {
      if (Array.isArray(page.elements)) {
        for (const element of page.elements) {
          if (
            element &&
            (element.type === "product" ||
              element.product_id ||
              element.product_image_id)
          ) {
            const product = {
              id:
                element.product_id ||
                (element.product_image as any)?.product_id,
              name: element.name,
              description: element.description,
              price: element.price_ht,
              product_image_id: element.product_image_id,
              brand_id: element.brand_id,
              image: (element as any).image,
              hook: element.hook,
            };
            products.push(product);
          }
        }
      }
    }
    if (products.length > 0) {
      supportStore.currentSupport.products = products;
    }
  }
});

const updateTitle = (event: FocusEvent): void => {
  const target = event.target as HTMLElement;
  titleText.value = target.textContent || "";
};

const updateValidityPeriod = (event: Event): void => {
  const target = event.target as HTMLElement;
  const text = target.textContent || "";
  validityPeriod.value = text;
};
// Fonctions pour contrôler la taille des titres de produits
const titleSizes = ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"];
const currentSizeIndex = computed(() => titleSizes.indexOf(selectedSize.value));

const increaseTitleSize = () => {
  const currentIndex = currentSizeIndex.value;
  if (currentIndex < titleSizes.length - 1) {
    selectedSize.value = titleSizes[currentIndex + 1];
  }
};

const decreaseTitleSize = () => {
  const currentIndex = currentSizeIndex.value;
  if (currentIndex > 0) {
    selectedSize.value = titleSizes[currentIndex - 1];
  }
};

// Fonction pour obtenir l'état actuel du composant
const getCurrentState = () => {
  // Mettre à jour les pages avec les produits convertis en éléments
  const products = supportStore.currentSupport?.products || [];
  const elements = products.map((product: any, index: number) => {
    if (!product) return null;

    // Récupérer les données de personnalisation depuis le store
    const customization = productCustomizationStore.getCustomization(
      product.id
    );

    return {
      type: "product",
      name: product.name,
      description: product.description || "",
      price_ht: product.price?.toString() || "0.00",
      price_ttc: product.price?.toString() || "0.00",
      price_old: "0.00",
      discount_mode: "0",
      hook: product.hook || product.name || "",
      pictogram_id: null,
      product_image_id: null,
      brand_id: null,
      product_id: product.id,
      element_settings: {
        // Inclure toutes les données de personnalisation
        removeBackground: customization.removeBackground,
        backgroundColor: customization.backgroundColor,
        showPrice: customization.showPrice,
        priceScale: customization.priceScale,
        priceSize: customization.priceSize,
        priceTTCSize: customization.priceTTCSize,
        pictogram: customization.pictogram,
        pictoScale: customization.pictoScale,
        brand: customization.brand,
        brandScale: customization.brandScale,
        header: customization.header,
        titleScale: customization.titleScale,
        position: customization.position,
        rotation: customization.rotation,
        zIndex: customization.zIndex,
        locked: false,
        opacity: 1,
        visible: true,
        blend_mode: "normal",
      },
      position_x: 0,
      position_y: 0,
      width: 100,
      height: 100,
      rotation: 0,
      z_index: index,
      custom_styles: [],
    };
  });

  // Mettre à jour les pages avec les éléments
  const updatedPages = supportStore.currentSupport?.pages.map(
    (page: any, pageIndex: number) => {
      if (pageIndex === 0) {
        // Seulement la première page pour les réseaux sociaux
        return {
          ...page,
          elements: elements,
        };
      }
      return page;
    }
  );

  return {
    name: titleText.value,
    dates: validityPeriod.value,
    pages: updatedPages,
    products: products,
  };
};

// Fonction pour générer le PDF aux dimensions exactes du support
const generatePdfBlob = async (): Promise<Blob> => {
  // Importer jsPDF dynamiquement
  const { default: jsPDF } = await import("jspdf");

  // Activer le mode preview temporairement
  const wasPreviewMode = supportStore.isPreview;
  if (!wasPreviewMode) {
    supportStore.togglePreview();
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  try {
    // Capturer le canvas
    const canvasElement = document.querySelector(
      ".support-canvas"
    ) as HTMLElement;
    if (!canvasElement) {
      throw new Error("Canvas non trouvé");
    }

    // Obtenir les dimensions en pixels pour facebook landscape
    const pixelDimensions = getSocialMediaPixelDimensions(
      "facebook",
      "landscape"
    );
    const { width: widthMm, height: heightMm } = calculateSocialMediaDimensions(
      pixelDimensions.width,
      pixelDimensions.height
    );

    // Utiliser dom-to-image pour capturer le canvas
    const { default: domtoimage } = await import("dom-to-image");
    const dataUrl = await domtoimage.toPng(canvasElement, {
      quality: 1.0,
      bgcolor: "#ffffff",
      width: pixelDimensions.width * HIGH_QUALITY_SCALE,
      height: pixelDimensions.height * HIGH_QUALITY_SCALE,
      style: {
        transform: `scale(${HIGH_QUALITY_SCALE})`,
        transformOrigin: "top left",
        width: `${pixelDimensions.width}px`,
        height: `${pixelDimensions.height}px`,
      },
    });

    // Créer le PDF aux dimensions exactes du support
    const pdf = new jsPDF({
      orientation: widthMm > heightMm ? "landscape" : "portrait",
      unit: "mm",
      format: [widthMm, heightMm],
      compress: false,
      precision: 2,
    });

    // Ajouter l'image aux dimensions exactes du support
    pdf.addImage(dataUrl, "PNG", 0, 0, widthMm, heightMm, undefined, PDF_IMAGE_COMPRESSION);

    return pdf.output("blob");
  } finally {
    // Restaurer le mode preview original
    if (!wasPreviewMode) {
      supportStore.togglePreview();
    }
  }
};

// Exposer les fonctions
defineExpose({
  generatePdfBlob,
  getCurrentState,
});
</script>

<script lang="ts">
export default {
  name: "SupportCanvas",
};
</script>

<style scoped>
.support-canvas {
  width: 1200px;
  height: 630px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.support-content {
  flex: 1;
  padding: 1.5rem;
  height: calc(630px - 120px);
  overflow: visible;
}

.products-container {
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: center;
}

.products-container.flex-col {
  flex-direction: row;
}

.product-zone {
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: visible;
  min-height: 0;
  flex: 1;
}

/* Style pour 2 produits */
.product-zone:only-child {
  max-width: 100%;
}

/* Style pour 2 produits */
.product-zone:first-child:nth-last-child(2),
.product-zone:first-child:nth-last-child(2) ~ .product-zone {
  max-width: calc(50% - 0.5rem);
}

/* Style pour 3 produits */
.product-zone:first-child:nth-last-child(3),
.product-zone:first-child:nth-last-child(3) ~ .product-zone {
  max-width: calc(33.33% - 0.67rem);
}

.product-zone:hover {
  border-color: #b70018;
}

.drop-active {
  border-color: #b70018;
  background-color: rgba(183, 0, 24, 0.1);
}

.product-zone.has-product {
  border-style: solid;
}

.product-zone.selected {
  background-color: #fff8dc;
}

/* Style pour les boutons de contrôle - et + */
.btn-control {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-control:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.btn-control:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mode aperçu */
.preview-mode .product-zone {
  border: none !important;
  background: transparent !important;
}

.preview-mode .product-zone:hover {
  border: none !important;
  background: transparent !important;
}

.preview-mode .drop-active {
  border: none !important;
  background: transparent !important;
}

/* Masquer les contrôles d'édition en mode aperçu */
.preview-mode :deep(.empty-state),
.preview-mode :deep(.remove-button),
.preview-mode :deep(.background-toggle),
.preview-mode :deep(.background-color-toggle),
.preview-mode :deep(.picto-controls),
.preview-mode :deep(.color-picker),
.preview-mode :deep(.toggle-price-button) {
  display: none !important;
}

/* Supprimer les bordures et fonds des zones interactives */
.preview-mode :deep(.picto-zone),
.preview-mode :deep(.brand-zone),
.preview-mode :deep(.image-container),
.preview-mode :deep(.product-image) {
  border: none !important;
  background: transparent !important;
  padding: 0 !important;
  cursor: default !important;
}

/* Rendre le contenu non éditable */
.preview-mode :deep([contenteditable="true"]) {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

/* Styles pour l'impression */
@media print {
  .support-canvas {
    box-shadow: none;
    margin: 0;
    padding: 0;
  }

  .product-zone {
    border: none !important;
    padding: 0;
  }

  .support-header,
  .support-footer {
    position: fixed;
    width: 100%;
    background-color: white;
    z-index: 100;
  }

  .support-header {
    top: 0;
  }

  .support-footer {
    bottom: 0;
  }

  .support-content {
    margin: 80px 0;
  }
}

.support-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.support-title-text {
  outline: none;
}

.preview-mode .support-title-container input[type="color"] {
  display: none;
}

.preview-mode .support-title-text[contenteditable="true"] {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

.color-picker-toggle {
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.color-picker-toggle:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.color-picker-toggle:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(183, 0, 24, 0.5);
}

.color-picker-toggle:active {
  background-color: #e5e7eb;
}

.hidden-color-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.preview-mode .color-picker-toggle {
  display: none !important;
}

/* Styles pour le pied de page */
.support-footer {
  border-top: 1px solid #e5e7eb;
  height: 60px;
}

[contenteditable="true"]:focus {
  outline: 2px solid #b70018;
  border-radius: 4px;
}

[contenteditable="true"]:empty:before {
  content: attr(placeholder);
  color: #9ca3af;
  cursor: text;
}

.toggle-validity-button {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  padding: 0;
}

.toggle-validity-button:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.toggle-validity-button .icon {
  font-size: 1rem;
}

.validity-text-container {
  width: 500px;
  height: 24px;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.validity-section {
  position: relative;
  width: 500px;
}
/* Décaler le logo marque vers la gauche pour les réseaux sociaux */
/* Positionner le logo marque à droite pour les réseaux sociaux */
:deep(.brand-zone) {
  position: absolute !important;
  bottom: 20px !important;
  right: 50px !important;
  left: auto !important;
  width: 100px !important;
  z-index: 10;
}

:deep(.brand-image) {
  max-width: 80px;
  max-height: 30px;
}

/* Espacement amélioré entre prix barré et prix HT pour éviter superposition lors du zoom */
:deep(.price-old-container) {
  margin-bottom: 12px !important;
}

:deep(.price-container) {
  margin-top: 8px !important;
}

:deep(.price-ttc-container) {
  margin-top: 10px !important;
}

/* Augmenter la taille des titres de produits */
:deep(.product-title) {
  font-size: 2.5rem !important;
  line-height: 1.2 !important;
}

/* Augmenter la taille des titres de produits pour les différentes grilles */
:deep(.products-container.grid-2 .product-title) {
  font-size: 2rem !important;
}

:deep(.products-container.grid-3 .product-title) {
  font-size: 1.8rem !important;
}
</style>
